import React from 'react';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { VvConnectFeesDto } from '../../../../slices/marketplace/loi-comparison/models/vv-connect-fees.model';
import { isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { getTotalCredits, getNetProjectedTotalFees, SfasFeeParams } from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import Helpers from '../../../../utils/helper';
import { SfasTransactionFeeTerms } from '../../../../components/SfasTransactionFeeTerms/SfasTransactionFeeTerms';

export const TOTAL_CREDITS_ROW_KEY = 'totalCredits';
export const NET_PROJECTED_TOTAL_FEES_ROW_KEY = 'netProjectedTotalFees';
export const TRANSACTION_FEE_ROW_KEY: keyof VvConnectFeesDto = 'transactionFeePercent';
export const SFAS_FEE_TERMS_ROW_CLASS = 'sfas-fee-terms-row';

export interface FeeRowConfig {
    label: string;
    key: keyof VvConnectFeesDto | typeof TOTAL_CREDITS_ROW_KEY | typeof NET_PROJECTED_TOTAL_FEES_ROW_KEY;
    format: 'currency' | 'percent';
    isBold?: boolean;
}

export const getFeeRows = (isSfasEngagement: boolean = false): FeeRowConfig[] => {
    const allRows: FeeRowConfig[] = [
        {
            label: isSfasEngagement ? 'Transaction Fee' : 'Transaction Fee %',
            key: TRANSACTION_FEE_ROW_KEY,
            format: 'percent',
        },
        { label: 'Projected Transaction Value', key: 'transactionFairValue', format: 'currency' },
        { label: 'Projected Transaction Fee $', key: 'transactionFeeDollar', format: 'currency' },
        { label: 'Application Fee', key: 'applicationFee', format: 'currency' },
        { label: 'Monthly Retainer Fees (already paid)', key: 'monthlyRetainerFees', format: 'currency' },
        { label: 'Process Fees', key: 'processFees', format: 'currency' },
        { label: 'Late / Delay Fees', key: 'lateDelayFees', format: 'currency' },
        { label: 'Engagement Suspension Fees', key: 'engagementSuspensionFees', format: 'currency' },
        { label: 'Total Credits', key: TOTAL_CREDITS_ROW_KEY, format: 'currency', isBold: true },
        { label: 'Projected Remaining Fees', key: NET_PROJECTED_TOTAL_FEES_ROW_KEY, format: 'currency', isBold: true },
    ];

    if (isSfasEngagement) {
        const keysToFilter = new Set(['applicationFee', 'processFees', 'lateDelayFees', 'engagementSuspensionFees']);
        return allRows.filter(row => !keysToFilter.has(row.key as string));
    }

    return allRows;
};

const formatFeeCurrency = (value: number | null | undefined): string => {
    if (value == null) return '';
    if (value === 0) return '$0';
    return Helpers.currencyFormat(value, false);
};

const formatFeePercent = (value: number | null | undefined): string => {
    if (value == null) return '';
    return `${parseFloat(value.toFixed(2))}%`;
};

const hasCompleteSfasFeeTerms = (sfasParams: SfasFeeParams | null | undefined): boolean =>
    !!sfasParams &&
    sfasParams.startLtmRevenue != null &&
    sfasParams.startLtmRevenue > 0 &&
    sfasParams.ltmRevenueThresholdPercent != null &&
    sfasParams.feePercentOnTotalFairValue != null &&
    sfasParams.feePercentOnExcessFairValue != null;

const renderTransactionFeeAmount = (sfasParams: SfasFeeParams | null | undefined) => {
    if (!hasCompleteSfasFeeTerms(sfasParams)) {
        return '—';
    }

    return (
        <SfasTransactionFeeTerms
            startLtmRevenue={sfasParams!.startLtmRevenue as number}
            ltmRevenueThresholdPercent={sfasParams!.ltmRevenueThresholdPercent}
            feePercentOnTotalFairValue={sfasParams!.feePercentOnTotalFairValue}
            feePercentOnExcessFairValue={sfasParams!.feePercentOnExcessFairValue}
        />
    );
};

interface VvConnectFeesSectionProps {
    fees: VvConnectFeesDto | null;
    portionPaidAtClose?: number | null;
    canEditRetainer?: boolean;
    onEditRetainer?: () => void;
    levelOfService?: number;
    projectedTransactionValue?: number | null;
    projectedTransactionFee?: number | null;
    sfasParams?: SfasFeeParams | null;
}

export const VvConnectFeesSection = ({
    fees,
    portionPaidAtClose,
    canEditRetainer,
    onEditRetainer,
    levelOfService,
    projectedTransactionValue,
    projectedTransactionFee,
    sfasParams,
}: VvConnectFeesSectionProps) => {
    const isSfasEngagement = isSfas(levelOfService);

    const netProjectedTotalFees = getNetProjectedTotalFees(
        fees,
        projectedTransactionFee ?? null,
        isSfasEngagement,
    );
    // Derived from the displayed rows so the subtraction always reads true on screen.
    const projectedDeferredPortion =
        netProjectedTotalFees != null && portionPaidAtClose != null
            ? netProjectedTotalFees - portionPaidAtClose
            : null;

    const getFeeValue = (row: FeeRowConfig): number | null | undefined => {
        if (row.key === TOTAL_CREDITS_ROW_KEY) {
            return getTotalCredits(fees);
        }
        if (row.key === NET_PROJECTED_TOTAL_FEES_ROW_KEY) {
            return netProjectedTotalFees;
        }
        if (row.key === 'transactionFairValue') {
            return projectedTransactionValue;
        }
        if (row.key === 'transactionFeeDollar') {
            return projectedTransactionFee;
        }
        return fees?.[row.key]?.fee;
    };

    const getCreditsValue = (row: FeeRowConfig): number | null | undefined => {
        if (row.key === TOTAL_CREDITS_ROW_KEY || row.key === NET_PROJECTED_TOTAL_FEES_ROW_KEY) {
            return null;
        }
        if (row.key === 'transactionFairValue' || row.key === 'transactionFeeDollar') {
            return null;
        }
        return fees?.[row.key]?.credits;
    };

    return (
        <table className="vv-connect-fees-table">
            <thead>
                <tr>
                    <th className="sub-col-header">Amount</th>
                    <th className="sub-col-header">Credits</th>
                </tr>
            </thead>
            <tbody>
                {getFeeRows(isSfasEngagement).map((row) => {
                    const isSfasFeeTermsRow = isSfasEngagement && row.key === TRANSACTION_FEE_ROW_KEY;
                    const rowClassName = [
                        row.isBold ? 'total-row' : null,
                        isSfasFeeTermsRow ? SFAS_FEE_TERMS_ROW_CLASS : null,
                    ].filter(Boolean).join(' ') || undefined;

                    if (isSfasFeeTermsRow) {
                        return (
                            <tr key={row.key} className={rowClassName}>
                                <td className="cell-value">{renderTransactionFeeAmount(sfasParams)}</td>
                                <td className="cell-value"></td>
                            </tr>
                        );
                    }

                    const feeStr = row.format === 'percent'
                        ? formatFeePercent(getFeeValue(row))
                        : formatFeeCurrency(getFeeValue(row));
                    const creditsStr = row.format === 'currency'
                        ? formatFeeCurrency(getCreditsValue(row))
                        : '';

                    const isEditableRetainerRow = row.key === 'monthlyRetainerFees' && canEditRetainer;

                    return (
                        <tr key={row.key} className={rowClassName}>
                            <td className="cell-value">
                                {feeStr}
                                {isEditableRetainerRow && (
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 ml-2"
                                        onClick={onEditRetainer}
                                        style={{ verticalAlign: 'baseline' }}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                )}
                            </td>
                            <td className="cell-value">{creditsStr}</td>
                        </tr>
                    );
                })}
                <tr className="total-row">
                    <td className="cell-value">{formatFeeCurrency(portionPaidAtClose)}</td>
                    <td className="cell-value"></td>
                </tr>
                {isSfasEngagement && (
                    <tr className="total-row">
                        <td className="cell-value">{formatFeeCurrency(projectedDeferredPortion)}</td>
                        <td className="cell-value"></td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};
